import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogTitle, DialogContent, useTheme, Grid, Card, CardContent, Typography, IconButton, CardActionArea } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import DashboardLayout from '../../components/layout/layout';
import { useGetResourcesByItem } from '../../hooks/api/user';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FolderIcon from '@mui/icons-material/Folder';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import LinkIcon from '@mui/icons-material/Link';
import BarChartIcon from '@mui/icons-material/BarChart';
import HelpIcon from '@mui/icons-material/Help';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import SensorsIcon from '@mui/icons-material/Sensors';
import StraightenIcon from '@mui/icons-material/Straighten';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import InfoIcon from '@mui/icons-material/Info';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AssignmentIcon from '@mui/icons-material/Assignment';

const CategoryPage: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId?: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Get resources for Valve & Switch Repair Kits (itemId: 46)
  const { data: repairKitsResources, isLoading: repairKitsLoading } = useGetResourcesByItem('46');

  // State for video dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);

  // Conditions for the 3 main categories and their subcategories
  const shouldFetchGeneralInformation = categoryId === 'GENERAL INFORMATION';
  const shouldFetchNewForms = categoryId === 'FORMS';
  
  // Subcategories for PRODUCT & MARKET REFERENCE
  const shouldFetchHydraulicProducts = categoryId === 'PRODUCT & MARKET REFERENCE' && (subcategoryId === 'HYDRAULIC PRODUCTS / ENERGY PRODUCTS' || subcategoryId === 'HYDRAULIC%20PRODUCTS%20%2F%20ENERGY%20PRODUCTS');
  const shouldFetchIndustrialProducts = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'INDUSTRIAL PRODUCTS';
  const shouldFetchSwitches = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'SWITCHES';
  const shouldFetchTransducer = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'TRANSDUCER/TRANSMITTER';
  const shouldFetchLevel = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'LEVEL';
  const shouldFetchDynalcoProducts = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'DYNALCO BRAND PRODUCTS';
  const shouldFetchMarketInformation = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'MARKET INFORMATION';
  const shouldFetchLegacyDocuments = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'LEGACY DOCUMENTS (REFERENCE)';
  
  // Sub-subcategories for HYDRAULIC PRODUCTS / ENERGY PRODUCTS
  const shouldFetchValve = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'VALVE';
  const shouldFetchRegulator = categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'REGULATOR';

  // Data fetching hooks for the 3 main categories
  const { data: generalInformationData, isLoading: isGeneralInformationLoading } = useGetResourcesByItem(
    shouldFetchGeneralInformation ? '39' : undefined
  );
  const { data: generalInformationData2, isLoading: isGeneralInformationLoading2 } = useGetResourcesByItem(
    shouldFetchGeneralInformation ? '36' : undefined
  );
  const { data: newFormsData, isLoading: isNewFormsLoading } = useGetResourcesByItem(
    shouldFetchNewForms ? '17' : undefined
  );

  // Data fetching for PRODUCT & MARKET REFERENCE subcategories
  const { data: hydraulicProductsData, isLoading: isHydraulicProductsLoading } = useGetResourcesByItem(
    shouldFetchHydraulicProducts ? '18' : undefined
  );
  const { data: industrialProductsData, isLoading: isIndustrialProductsLoading } = useGetResourcesByItem(
    shouldFetchIndustrialProducts ? '19' : undefined
  );
  
  // SWITCHES subcategory API calls
  const { data: switchesData1, isLoading: isSwitchesLoading1 } = useGetResourcesByItem(
    shouldFetchSwitches ? '45' : undefined
  );
  const { data: switchesData2, isLoading: isSwitchesLoading2 } = useGetResourcesByItem(
    shouldFetchSwitches ? '56' : undefined
  );
  const { data: switchesData3, isLoading: isSwitchesLoading3 } = useGetResourcesByItem(
    shouldFetchSwitches ? '65' : undefined
  );
  const { data: switchesData4, isLoading: isSwitchesLoading4 } = useGetResourcesByItem(
    shouldFetchSwitches ? '18' : undefined
  );
  const { data: switchesData5, isLoading: isSwitchesLoading5 } = useGetResourcesByItem(
    shouldFetchSwitches ? '16' : undefined
  );
  
  // TRANSDUCER/TRANSMITTER subcategory API calls
  const { data: transducerData1, isLoading: isTransducerLoading1 } = useGetResourcesByItem(
    shouldFetchTransducer ? '45' : undefined
  );
  const { data: transducerData2, isLoading: isTransducerLoading2 } = useGetResourcesByItem(
    shouldFetchTransducer ? '65' : undefined
  );
  const { data: transducerData3, isLoading: isTransducerLoading3 } = useGetResourcesByItem(
    shouldFetchTransducer ? '16' : undefined
  );
  const { data: transducerData4, isLoading: isTransducerLoading4 } = useGetResourcesByItem(
    shouldFetchTransducer ? '64' : undefined
  );
  const { data: transducerData5, isLoading: isTransducerLoading5 } = useGetResourcesByItem(
    shouldFetchTransducer ? '63' : undefined
  );
  
  // LEVEL subcategory API calls
  const { data: levelData1, isLoading: isLevelLoading1 } = useGetResourcesByItem(
    shouldFetchLevel ? '16' : undefined
  );
  const { data: levelData2, isLoading: isLevelLoading2 } = useGetResourcesByItem(
    shouldFetchLevel ? '18' : undefined
  );
  const { data: levelData3, isLoading: isLevelLoading3 } = useGetResourcesByItem(
    shouldFetchLevel ? '45' : undefined
  );
  const { data: levelData4, isLoading: isLevelLoading4 } = useGetResourcesByItem(
    shouldFetchLevel ? '65' : undefined
  );
  
  const { data: dynalcoProductsData, isLoading: isDynalcoProductsLoading } = useGetResourcesByItem(
    shouldFetchDynalcoProducts ? '65' : undefined
  );
  const { data: dynalcoProductsData2, isLoading: isDynalcoProductsLoading2 } = useGetResourcesByItem(
    shouldFetchDynalcoProducts ? '45' : undefined
  );
  const { data: marketInformationData, isLoading: isMarketInformationLoading } = useGetResourcesByItem(
    shouldFetchMarketInformation ? '65' : undefined
  );
  const { data: marketInformationData2, isLoading: isMarketInformationLoading2 } = useGetResourcesByItem(
    shouldFetchMarketInformation ? '45' : undefined
  );
  const { data: legacyDocumentsData, isLoading: isLegacyDocumentsLoading } = useGetResourcesByItem(
    shouldFetchLegacyDocuments ? '16' : undefined
  );
  const { data: legacyDocumentsData2, isLoading: isLegacyDocumentsLoading2 } = useGetResourcesByItem(
    shouldFetchLegacyDocuments ? '18' : undefined
  );
  const { data: legacyDocumentsData3, isLoading: isLegacyDocumentsLoading3 } = useGetResourcesByItem(
    shouldFetchLegacyDocuments ? '62' : undefined
  );
  const { data: legacyDocumentsData4, isLoading: isLegacyDocumentsLoading4 } = useGetResourcesByItem(
    shouldFetchLegacyDocuments ? '17' : undefined
  );

  // Data fetching for VALVE and REGULATOR sub-subcategories
  const { data: valveData, isLoading: isValveLoading } = useGetResourcesByItem(
    shouldFetchValve ? '65' : undefined
  );
  const { data: valveData2, isLoading: isValveLoading2 } = useGetResourcesByItem(
    shouldFetchValve ? '16' : undefined
  );
  const { data: valveData3, isLoading: isValveLoading3 } = useGetResourcesByItem(
    shouldFetchValve ? '45' : undefined
  );
  const { data: valveData4, isLoading: isValveLoading4 } = useGetResourcesByItem(
    shouldFetchValve ? '56' : undefined
  );
  const { data: regulatorData, isLoading: isRegulatorLoading } = useGetResourcesByItem(
    shouldFetchRegulator ? '16' : undefined
  );
  const { data: regulatorData2, isLoading: isRegulatorLoading2 } = useGetResourcesByItem(
    shouldFetchRegulator ? '45' : undefined
  );
  const { data: regulatorData3, isLoading: isRegulatorLoading3 } = useGetResourcesByItem(
    shouldFetchRegulator ? '56' : undefined
  );

  const handleBack = () => {
    if (subcategoryId) {
      // Special case: If we're on VALVE or REGULATOR pages, go back to HYDRAULIC PRODUCTS / ENERGY PRODUCTS
      if (categoryId === 'PRODUCT & MARKET REFERENCE' && (subcategoryId === 'VALVE' || subcategoryId === 'REGULATOR')) {
        navigate(`/resources/category/${categoryId}/HYDRAULIC%20PRODUCTS%20%2F%20ENERGY%20PRODUCTS`);
      } 
      // Special case: If we're on SWITCHES, TRANSDUCER/TRANSMITTER, or LEVEL pages, go back to INDUSTRIAL PRODUCTS
      else if (categoryId === 'PRODUCT & MARKET REFERENCE' && (subcategoryId === 'SWITCHES' || subcategoryId === 'TRANSDUCER/TRANSMITTER' || subcategoryId === 'LEVEL')) {
        navigate(`/resources/category/${categoryId}/INDUSTRIAL%20PRODUCTS`);
      } else {
      // If we're on a subcategory page, go back to the category page
      navigate(`/resources/category/${categoryId}`);
      }
    } else {
      // If we're on a category page, go back to the main resources page
      navigate('/resources');
    }
  };

  const handleSubcategoryClick = (subcategory: string) => {
    const encodedSubcategory = encodeURIComponent(subcategory);
    navigate(`/resources/category/${categoryId}/${encodedSubcategory}`);
  };

  const handleVideoClick = (item: any) => {
    setSelectedVideo(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVideo(null);
  };

  // Function to handle home navigation
  const handleHomeClick = () => {
    navigate('/resources');
  };

  // Reusable home button component for top of page
  const TopHomeButton = () => (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={handleHomeClick}
        sx={{
          bgcolor: '#c8102e',
          textTransform: 'none',
          fontWeight: 600,
          ...theme.typography.button,
          '&:hover': { bgcolor: '#a30f24' },
        }}
      >
        Sales Tools Home
      </Button>
    </Box>
  );

  // Function to get appropriate icon for subcategories
  const getSubcategoryIcon = (subcategory: string) => {
    const subcategoryLower = subcategory.toLowerCase();
    
    if (subcategoryLower.includes('hydraulic') || subcategoryLower.includes('energy')) {
      return <BuildIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('industrial')) {
      return <EngineeringIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('dynalco')) {
      return <BusinessIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('market')) {
      return <BarChartIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('legacy')) {
      return <FolderIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('valve')) {
      return <SettingsIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('regulator')) {
      return <TuneIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('switches')) {
      return <ToggleOnIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('transducer') || subcategoryLower.includes('transmitter')) {
      return <SensorsIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (subcategoryLower.includes('level')) {
      return <StraightenIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else {
      return <FolderIcon sx={{ fontSize: 24, color: '#000000' }} />;
    }
  };

  // Function to get appropriate icon for individual resources
  const getResourceIcon = (item: any) => {
    const filename = item.filename || item.title || '';
    const filenameLower = filename.toLowerCase();
    
    // Specific icons for Dynalco items
    if (filenameLower.includes('s5 training series - dynalco magnetic pickups')) {
      return <PlayCircleIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('dynalco - product overview')) {
      return <InfoIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('dynalco magnetic pickup sell sheet marathon')) {
      return <DescriptionIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('dynalco magnetic round instrument sell sheet')) {
      return <AssignmentIcon sx={{ fontSize: 24, color: '#000000' }} />;
    }
    
    // Use generic icons based on content type, not file extension
    if (filenameLower.includes('video') || filenameLower.includes('training') || filenameLower.includes('presentation')) {
      return <VideoLibraryIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('manual') || filenameLower.includes('guide') || filenameLower.includes('instruction')) {
      return <MenuBookIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('form') || filenameLower.includes('request')) {
      return <ArticleIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('support') || filenameLower.includes('help') || filenameLower.includes('contact')) {
      return <HelpIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('engineering') || filenameLower.includes('technical') || filenameLower.includes('design')) {
      return <EngineeringIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('assessment') || filenameLower.includes('analysis') || filenameLower.includes('report')) {
      return <BarChartIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (filenameLower.includes('business') || filenameLower.includes('commercial') || filenameLower.includes('sales')) {
      return <BusinessIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else if (item.link) {
      return <LinkIcon sx={{ fontSize: 24, color: '#000000' }} />;
    } else {
      // Default generic document icon for all file types
      return <DescriptionIcon sx={{ fontSize: 24, color: '#000000' }} />;
    }
  };

  if (isGeneralInformationLoading || isGeneralInformationLoading2 || isNewFormsLoading || isHydraulicProductsLoading || isIndustrialProductsLoading || isSwitchesLoading1 || isSwitchesLoading2 || isSwitchesLoading3 || isSwitchesLoading4 || isSwitchesLoading5 || isTransducerLoading1 || isTransducerLoading2 || isTransducerLoading3 || isTransducerLoading4 || isTransducerLoading5 || isLevelLoading1 || isLevelLoading2 || isLevelLoading3 || isLevelLoading4 || isDynalcoProductsLoading || isDynalcoProductsLoading2 || isMarketInformationLoading || isMarketInformationLoading2 || isLegacyDocumentsLoading || isLegacyDocumentsLoading2 || isLegacyDocumentsLoading3 || isLegacyDocumentsLoading4 || isValveLoading || isValveLoading2 || isValveLoading3 || isValveLoading4 || isRegulatorLoading || isRegulatorLoading2 || isRegulatorLoading3) {
    return (
      <DashboardLayout 
        title="Loading..." 
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools"
      >
        <TopHomeButton />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading...</div>
        </Box>
      </DashboardLayout>
    );
  }

  // If this is PRODUCT & MARKET REFERENCE category
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && !subcategoryId) {
    const subcategories = [
      'HYDRAULIC PRODUCTS / ENERGY PRODUCTS',
      'INDUSTRIAL PRODUCTS',
      'DYNALCO BRAND PRODUCTS',
      'MARKET INFORMATION',
      'LEGACY DOCUMENTS (REFERENCE)'
    ];

    return (
      <DashboardLayout 
        title="Product & Market Reference" 
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {subcategories.map((subcategory, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSubcategoryClick(subcategory)}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getSubcategoryIcon(subcategory)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {subcategory}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  // If this is HYDRAULIC PRODUCTS / ENERGY PRODUCTS subcategory, show sub-subcategories
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && (subcategoryId === 'HYDRAULIC PRODUCTS / ENERGY PRODUCTS' || subcategoryId === 'HYDRAULIC%20PRODUCTS%20%2F%20ENERGY%20PRODUCTS')) {
    const subcategories = [
      'VALVE',
      'REGULATOR'
    ];

      return (
        <DashboardLayout 
        title="Hydraulic Products / Energy Products" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Hydraulic Products / Energy Products"
        >
          <TopHomeButton />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
          {subcategories.map((subcategory, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSubcategoryClick(subcategory)}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getSubcategoryIcon(subcategory)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {subcategory}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
            ))}
          </Box>

          {/* Back Button */}
          <Box sx={{ textAlign: 'left', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={{
                bgcolor: '#c8102e',
                textTransform: 'none',
                fontWeight: 600,
                ...theme.typography.button,
                '&:hover': { bgcolor: '#a30f24' },
              }}
            >
              BACK
            </Button>
          </Box>
        </DashboardLayout>
      );
  }

  // If this is INDUSTRIAL PRODUCTS subcategory, show sub-subcategories
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'INDUSTRIAL PRODUCTS') {
    const subcategories = [
      'SWITCHES',
      'TRANSDUCER/TRANSMITTER',
      'LEVEL'
    ];

    return (
      <DashboardLayout 
        title="Industrial Products" 
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Industrial Products"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {subcategories.map((subcategory, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSubcategoryClick(subcategory)}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getSubcategoryIcon(subcategory)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {subcategory}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  // If this is SWITCHES subcategory, show items from 4 APIs
            if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'SWITCHES') {
              const allItems1 = switchesData1 || [];
              const allItems2 = switchesData2 || [];
              const allItems3 = switchesData3 || [];
              const allItems4 = switchesData4 || [];
              const allItems5 = switchesData5 || [];
              const combinedItems = [...allItems1, ...allItems2, ...allItems3, ...allItems4, ...allItems5];
              
              console.log('SWITCHES API 45 items:', allItems1);
              console.log('SWITCHES API 56 items:', allItems2);
              console.log('SWITCHES API 65 items:', allItems3);
              console.log('SWITCHES API 18 items:', allItems4);
              console.log('SWITCHES API 16 items:', allItems5);
              console.log('SWITCHES Combined items:', combinedItems);
              
              // Filter to show only specific items - SWITCHES section
              const filteredItems = combinedItems.filter((item: any) => {
                const title = item.title || '';
                return title.includes('Series 9681X, 9692X, 9671X Product Flyer') ||
                       title.includes('T9692X Compact Explosion Proof Temperature Switch Training') ||
                       title.includes('T9692X Compact Explosion Proof Temperature Switch Product Launch') ||
                       title.includes('Product HighLights - Volume 1, Issue 03') ||
                       title.includes('Product HighLights - Volume 1, Issue 04') ||
                       title.includes('Product HighLights - Volume 1, Issue 10') ||
                       title.includes('Product HighLights - Volume 1, Issue 01') ||
                       title.includes('Product HighLights - Volume 1, Issue 02') ||
                       title.includes('Product HighLights - Volume 1, Issue 05') ||
                       title.includes('Barksdale S5 Training Module 5 Mechanical Pressure Switches') ||
                       title.includes('Pressure Switch Adjustment Instructions') ||
                       title.includes('Pressure Switch Test Procedure') ||
                       title.includes('Temperature Switch Adjustment Instructions');
              });

    return (
      <DashboardLayout 
        title="Switches"
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Industrial Products > Switches"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {filteredItems.map((item: any, index: number) => {
            // Custom display titles for Product Highlights items
            let displayTitle = item.title;
            if (item.title.includes('Product HighLights - Volume 1, Issue 03')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 03 TXR & TXL Xproof Temperature Switch - Heat Tracing: Temperature Control to Support High Load Systems';
            } else if (item.title.includes('Product HighLights - Volume 1, Issue 04')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 04 T9692X Xproof Temperature Switch - Natural Draft Process Heaters: Exhaust Temperature Control';
            } else if (item.title.includes('Product HighLights - Volume 1, Issue 10')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 10 B1X Xproof Pressure Switch - Pump Transfer Skid: Safety Shutdown';
            } else if (item.title.includes('Product HighLights - Volume 1, Issue 01')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 01 E1H Pressure Switch - Steam Generators: High Pressure with Manual Reset';
            } else if (item.title.includes('Product HighLights - Volume 1, Issue 02')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 02 E1H Pressure Switch - Medical Gas Supply: Self Regulating Safety Control';
            } else if (item.title.includes('Product HighLights - Volume 1, Issue 05')) {
              displayTitle = 'Product HighLights - Volume 1, Issue 05 MSPS Pressure Switch - Beverage Dispensers: Monitoring Supply';
            }

            return (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Debug logging for the specific item
                  if (item.title && item.title.includes('T9692X Compact Explosion Proof Temperature Switch Training')) {
                    console.log('T9692X Training item clicked:', item);
                    console.log('downloadUrl:', item.downloadUrl);
                    console.log('filename:', item.filename);
                  }
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    
                    // Check if it's a video file
                    const isVideo = item.filename.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
                    
                    if (isVideo) {
                      // For videos, open in dialog
                      handleVideoClick(item);
                    } else {
                      // For other files (PDFs, etc.), open in new tab
                      window.open(absoluteUrl, '_blank');
                    }
                  } else {
                    console.error('Item missing downloadUrl or filename:', item);
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {displayTitle}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
            );
          })}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is TRANSDUCER/TRANSMITTER subcategory, show items from 5 APIs
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'TRANSDUCER/TRANSMITTER') {
    const allItems1 = transducerData1 || [];
    const allItems2 = transducerData2 || [];
    const allItems3 = transducerData3 || [];
    const allItems4 = transducerData4 || [];
    const allItems5 = transducerData5 || [];
    const combinedItems = [...allItems1, ...allItems2, ...allItems3, ...allItems4, ...allItems5];
    
    console.log('TRANSDUCER API 45 items:', allItems1);
    console.log('TRANSDUCER API 65 items:', allItems2);
    console.log('TRANSDUCER API 16 items:', allItems3);
    console.log('TRANSDUCER API 64 items:', allItems4);
    console.log('TRANSDUCER API 63 items:', allItems5);
    console.log('TRANSDUCER Combined items:', combinedItems);
    
    // Filter to show only specific items - TRANSDUCER/TRANSMITTER section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('BHyT Series Fuel Cell Product Flyer') ||
             title.includes('BHyT Series ReFuel Product Flyer') ||
             title.includes('BiT Series 450E Product Flyer') ||
             title.includes('BiT Series 450X Product Flyer') ||
             title.includes('Enhancing Media Compatibility in Pressure Instruments') ||
             title.includes('Barksdale S5 Training Module 1 BiT & BoT Pressure Transducers') ||
             title.includes('2022 BoT Series BTX Prerecorded Training') ||
             title.includes('Optimizing Performance Machine Tools') ||
             title.includes('Monolithic Pressure Sensors vs. Gold-Plated Piezo Sensors');
    });

    return (
      <DashboardLayout 
        title="Transducer/Transmitter"
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Industrial Products > Transducer/Transmitter"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    
                    // Check if it's a video file
                    const isVideo = item.filename.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
                    
                    if (isVideo) {
                      // For videos, open in dialog
                      handleVideoClick(item);
                    } else {
                      // For other files (PDFs, etc.), open in new tab
                      window.open(absoluteUrl, '_blank');
                    }
                  } else {
                    console.error('Item missing downloadUrl or filename:', item);
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
              <Button
              onClick={handleCloseDialog}
                sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                  '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
              ✕
              </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
        </DashboardLayout>
      );
  }

  // If this is LEVEL subcategory, show items from 4 APIs
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'LEVEL') {
    const allItems1 = levelData1 || [];
    const allItems2 = levelData2 || [];
    const allItems3 = levelData3 || [];
    const allItems4 = levelData4 || [];
    const combinedItems = [...allItems1, ...allItems2, ...allItems3, ...allItems4];
    
    console.log('LEVEL API 16 items:', allItems1);
    console.log('LEVEL API 18 items:', allItems2);
    console.log('LEVEL API 45 items:', allItems3);
    console.log('LEVEL API 65 items:', allItems4);
    console.log('LEVEL Combined items:', combinedItems);
    
    // Filter to show only specific items - LEVEL section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('Submersible Level Transmitter Product Launch') ||
             title.includes('Specific Gravity Chart') ||
             title.includes('Level Product Overview') ||
             title.includes('Barksdale S5 Training Module 3 Liquid Measurement & Control') ||
             title.includes('S5 Training Series - Submersible Level Transmitter');
    });

    return (
      <DashboardLayout 
        title="Level"
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Industrial Products > Level"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    
                    // Check if it's a video file
                    const isVideo = item.filename.toLowerCase().match(/\.(mp4|avi|mov|wmv|flv|webm)$/);
                    
                    if (isVideo) {
                      // For videos, open in dialog
                      handleVideoClick(item);
                    } else {
                      // For other files (PDFs, etc.), open in new tab
                      window.open(absoluteUrl, '_blank');
                    }
                  } else {
                    console.error('Item missing downloadUrl or filename:', item);
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is DYNALCO BRAND PRODUCTS subcategory
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'DYNALCO BRAND PRODUCTS') {
    const allItems1 = dynalcoProductsData || [];
    const allItems2 = dynalcoProductsData2 || [];
    const combinedItems = [...allItems1, ...allItems2];
    
    // Debug: Log the items to see what we're getting
    console.log('DYNALCO API 65 items:', allItems1);
    console.log('DYNALCO API 45 items:', allItems2);
    console.log('DYNALCO Combined items:', combinedItems);
    
    // Filter to show only specific items - DYNALCO BRAND PRODUCTS section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('S5 Training Series - Dynalco Magnetic Pickups') ||
             title.includes('Dynalco - Product Overview') ||
             title.includes('Dynalco Magnetic Pickup Sell Sheet Marathon') ||
             title.includes('Dynalco Magnetic Round Instrument Sell Sheet');
    });

      return (
        <DashboardLayout 
        title="Dynalco Brand Products" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Dynalco Brand Products"
        >
          <TopHomeButton />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  // Check if it's a video file (.mp4)
                  if (item.downloadUrl && item.filename && item.filename.toLowerCase().endsWith('.mp4')) {
                    handleVideoClick(item);
                    return;
                  }
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  } else if (item.link) {
                    window.open(item.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title || item.filename}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is MARKET INFORMATION subcategory
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'MARKET INFORMATION') {
    const allItems1 = marketInformationData || [];
    const allItems2 = marketInformationData2 || [];
    const combinedItems = [...allItems1, ...allItems2];
    
    // Debug: Log the items to see what we're getting
    console.log('MARKET INFO API 65 items:', allItems1);
    console.log('MARKET INFO API 45 items:', allItems2);
    console.log('MARKET INFO Combined items:', combinedItems);
    
    // Filter to show only specific items - MARKET INFORMATION section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('Barksdale S5 Channel Partner Training - Water Specific Applications') ||
             title.includes('Brochure - Marine Products') ||
             title.includes('Brochure - Compressor Products') ||
             title.includes('Brochure - Fluid Power Products') ||
             title.includes('Brochure - Barksdale Product Overview') ||
             title.includes('Hydrogen - Market Overview') ||
             title.includes('Hydraulic Power Unit') ||
             title.includes('Brochure - Oil and Gas Product') ||
             title.includes('Brochure - Transportation Products') ||
             title.includes('Brochure - ASV Aftermarket Replacement Catalog');
    });

    return (
      <DashboardLayout 
        title="Market Information" 
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Market Information"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  // Check if it's a video file (.mp4)
                  if (item.downloadUrl && item.filename && item.filename.toLowerCase().endsWith('.mp4')) {
                    handleVideoClick(item);
                    return;
                  }
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  } else if (item.link) {
                    window.open(item.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title || item.filename}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is LEGACY DOCUMENTS (REFERENCE) subcategory
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'LEGACY DOCUMENTS (REFERENCE)') {
    const allItems1 = legacyDocumentsData || [];
    const allItems2 = legacyDocumentsData2 || [];
    const allItems3 = legacyDocumentsData3 || [];
    const allItems4 = legacyDocumentsData4 || [];
    const combinedItems = [...allItems1, ...allItems2, ...allItems3, ...allItems4];
    
    // Debug: Log the items to see what we're getting
    console.log('LEGACY API 16 items:', allItems1);
    console.log('LEGACY API 18 items:', allItems2);
    console.log('LEGACY API 62 items:', allItems3);
    console.log('LEGACY API 17 items:', allItems4);
    console.log('LEGACY Combined items:', combinedItems);
    
    // Filter to show only specific items - LEGACY DOCUMENTS (REFERENCE) section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('Diaphragm Seal PowerPoint Training Presentation') ||
             title.includes('Diaphragm Seal Online Training Presentation') ||
             title.includes('UDS7 Programming Instructions') ||
             title.includes('Barksdale MSPSFF Specification Change Notice') ||
             title.includes('Transducer Competitive Cross Reference') ||
             title.includes('Level Competitive Cross Reference') ||
             title.includes('Valve Competitive Cross Reference') ||
             title.includes('Beloit Valve Cross Reference') ||
             title.includes('Temperature Switch Competitive Cross Reference') ||
             title.includes('Pressure Switch Competitive Cross Reference') ||
             title.includes('Series 600 Transducer Competitive Cross Reference') ||
             title.includes('Competitor Crossover Reference') ||
             title.includes('Product Family Competitive Cross');
    });

      return (
        <DashboardLayout 
        title="Legacy Documents (Reference)" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Legacy Documents (Reference)"
        >
          <TopHomeButton />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  // Check if it's a video file (.mp4)
                  if (item.downloadUrl && item.filename && item.filename.toLowerCase().endsWith('.mp4')) {
                    handleVideoClick(item);
                    return;
                  }
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  } else if (item.link) {
                    window.open(item.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title || item.filename}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is VALVE sub-subcategory, show items from API
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'VALVE') {
    const allItems1 = valveData || [];
    const allItems2 = valveData2 || [];
    const allItems3 = valveData3 || [];
    const allItems4 = valveData4 || [];
    const combinedItems = [...allItems1, ...allItems2, ...allItems3, ...allItems4];
    
    // Filter to show only specific items - VALVE section
    const filteredItems = combinedItems.filter((item: any) => 
      item.title === 'S5 Training Module 4 Valve Products' ||
      item.title === 'Pressure Relief Valve Training' ||
      item.title === 'Position Indicator Product Launch' ||
      item.title === 'Series 145 Product Flyer' ||
      item.title === '*New! Series III-L Actuated Heavy Duty Valves Launch Package*' ||
      item.title === '*New! Series III-L Actuated Heavy Duty Valves – Online Presentation*'
    );

      return (
        <DashboardLayout 
        title="Valve" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Hydraulic Products / Energy Products > Valve"
        >
          <TopHomeButton />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  // Check if it's a video file (.mp4)
                  if (item.downloadUrl && item.filename && item.filename.toLowerCase().endsWith('.mp4')) {
                    handleVideoClick(item);
                    return;
                  }
                  
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  } else if (item.link) {
                    window.open(item.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title || item.filename}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  // If this is REGULATOR sub-subcategory, show items from API
  if (categoryId === 'PRODUCT & MARKET REFERENCE' && subcategoryId === 'REGULATOR') {
    const allItems1 = regulatorData || [];
    const allItems2 = regulatorData2 || [];
    const allItems3 = regulatorData3 || [];
    const combinedItems = [...allItems1, ...allItems2, ...allItems3];
    
    // Debug: Log the items to see what we're getting
    console.log('REGULATOR API 16 items:', allItems1);
    console.log('REGULATOR API 45 items:', allItems2);
    console.log('REGULATOR API 56 items:', allItems3);
    console.log('REGULATOR Combined items:', combinedItems);
    
    // Filter to show only specific items - REGULATOR section
    const filteredItems = combinedItems.filter((item: any) => {
      const title = item.title || '';
      return title.includes('1-1/2" Hydraulic Pressure Regulator Product Launch') ||
             title.includes('Series 20415 Product Flyer') ||
             title.includes('Series S20517') ||
             title.includes('Stainless Steel Regulator');
    });

      return (
        <DashboardLayout 
        title="Regulator" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Product & Market Reference > Hydraulic Products / Energy Products > Regulator"
        >
          <TopHomeButton />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(3),
                }}
              >
              {filteredItems.map((item: any, index: number) => (
                <Card
                  key={index}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)',
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      // Check if it's a video file (.mp4)
                      if (item.downloadUrl && item.filename && item.filename.toLowerCase().endsWith('.mp4')) {
                        handleVideoClick(item);
                        return;
                      }
                      
                      if (item.downloadUrl && item.filename) {
                        const absoluteUrl = item.downloadUrl.startsWith('http') 
                          ? item.downloadUrl 
                          : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                        window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                      } else if (item.link) {
                        window.open(item.link, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    sx={{
                      p: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        '&:last-child': {
                          pb: 3,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48,
                          height: 48,
                          borderRadius: 1,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: theme.palette.primary.main,
                        }}
                      >
                        {getResourceIcon(item)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            ...theme.typography.body1,
                          }}
                        >
                          {item.title || item.filename}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>

          {/* Back Button */}
          <Box sx={{ textAlign: 'left', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={{
                bgcolor: '#c8102e',
                textTransform: 'none',
                fontWeight: 600,
                ...theme.typography.button,
                '&:hover': { bgcolor: '#a30f24' },
              }}
            >
              BACK
            </Button>
          </Box>

        {/* Video Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000',
              p: 0,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedVideo?.title}</span>
            <Button
              onClick={handleCloseDialog}
              sx={{
                color: '#fff',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              ✕
            </Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
            {selectedVideo && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                }}
              >
                <source
                  src={
                    selectedVideo.downloadUrl?.startsWith('http')
                      ? selectedVideo.downloadUrl
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${selectedVideo.downloadUrl}`
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )}
          </DialogContent>
        </Dialog>
        </DashboardLayout>
      );
  }

  // If this is GENERAL INFORMATION category
  if (categoryId === 'GENERAL INFORMATION') {
    const allItems1 = generalInformationData || [];
    const allItems2 = generalInformationData2 || [];
    const combinedItems = [...allItems1, ...allItems2];
    
    const filteredItems = combinedItems.filter((item: any) => 
      item.title === 'Barksdale General Overview - 2024' ||
      item.title === 'Standard Conditions of Sale' ||
      item.title === 'General Sales Policy' ||
      item.title === 'Channel Partner Agreement ' || // Added trailing space for exact match
      item.title === 'Channel Partner Agreement'     // Fallback without trailing space
    );

      return (
        <DashboardLayout 
        title="General Information" 
          activeMenu="resources"
          initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > General Information"
        >
          <TopHomeButton />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
          {filteredItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  } else if (item.link) {
                    window.open(item.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  // If this is FORMS category
  if (categoryId === 'FORMS') {
    const allItems = newFormsData || [];
    const filteredItems = allItems.filter((item: any) => 
      item.title === 'Failure Analysis Request' // Filters by actual database title
    );

    // Static FORMS items from NewSalesTools configuration
    const staticFormsItems = [
      {
        title: 'Valve Repair Kits',
        filename: 'Valve Repair Kits - 10-9-2025.xlsx',
        downloadUrl: null, // Will be handled by click
        isStatic: true,
        staticType: 'Valve Repair Kit 2025'
      }
    ];

    // Combine database items and static items
    const allFormsItems = [...filteredItems, ...staticFormsItems];

    return (
      <DashboardLayout 
        title="Forms / Repair Kits" 
        activeMenu="resources"
        initialBreadcrumbs="Sales Tools"
        breadcrumbs="Sales Tools > Forms / Repair Kits"
      >
        <TopHomeButton />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {allFormsItems.map((item: any, index: number) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardActionArea
                onClick={() => {
                  if (item.isStatic) {
                    // Handle static items
                    if (item.staticType === 'Valve Repair Kit 2025') {
                      // Use the hook data instead of raw fetch
                      if (repairKitsResources && Array.isArray(repairKitsResources) && repairKitsResources.length > 0) {
                        // Use the 2025 file (itemId: 658)
                        const resource = repairKitsResources.find(r => r.itemId === 658);
                        if (resource.downloadUrl) {
                          // Download the file
                          const downloadUrl = resource.downloadUrl.startsWith('http') 
                            ? resource.downloadUrl 
                            : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${resource.downloadUrl}`;
                          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                        } else {
                          alert('No download file available for Valve Repair Kit 2025');
                        }
                      } else if (repairKitsLoading) {
                        alert('Loading Valve Repair Kit 2025...');
                      } else {
                        alert('No download file available for Valve Repair Kit 2025');
                      }
                    }
                  } else if (item.downloadUrl && item.filename) {
                    const absoluteUrl = item.downloadUrl.startsWith('http') 
                      ? item.downloadUrl 
                      : `${process.env.REACT_APP_API_URL || window.location.origin.replace(':3000', ':8080')}${item.downloadUrl}`;
                    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
                sx={{
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': {
                      pb: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: theme.palette.primary.main,
                    }}
                  >
                    {getResourceIcon(item)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        ...theme.typography.body1,
                      }}
                    >
                      {item.title === 'Failure Analysis Request' ? 'Failure Analysis Forms' : (item.title || item.filename)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Back Button */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  // Default fallback
  return (
    <DashboardLayout 
      title="Category" 
      activeMenu="resources"
      initialBreadcrumbs="Sales Tools"
      breadcrumbs="Sales Tools"
    >
      <TopHomeButton />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div>Category not found</div>
      </Box>
    </DashboardLayout>
  );
};

export default CategoryPage;